/*
 * @Description:抽离swiper-item.vue组件的灯塔上报逻辑
 * @Author: fiveonelei
 * @Date: 2022-03-24 21:21:16
 * @FilePath: /vise/packages/app-share/src/composable/use-compoent-report-methods/swiper-item-report.ts
 */
import { watch, Ref, computed } from 'vue';
import { useStore } from '@/store/index';
import { ModelMetaFeed } from '@/types/model';
import useGetCommonMethods from '@/composable/use-get-common-method';
import useProvideUrlInfo from '@/composable/use-provide-url-info';
import { report } from '@/libs/report';
import { EMiniAppPosition } from '@/constants/mini-app';
import { VideoJsPlayer } from 'video.js';
import { IHashMap } from '@/types/data';
import { IS_SSR } from '@/utils/env';

type Params = {
  feed: Ref<ModelMetaFeed>,
  playerRef: Ref<null | { player: VideoJsPlayer}>,
  currentTime: Ref<number>,
};

type OnEndedParams = {
  isLastFeed: boolean,
};

export default function userSwiperItemReport(params: Params) {
  const { state } = useStore();
  const { feed } = params;
  const feedReportOpt = {
    video_id: feed.value?.video?.fileId,
    owner_id: feed.value?.poster?.id,
  };
  const playReportOpt = {
    video_id: feed.value?.id,
    owner_id: feed.value?.posterId,
    video_length: feed.value?.video?.duration,
  };
  const { getMiniAppReportValue } = useGetCommonMethods();
  const { info: urlInfo } = useProvideUrlInfo();
  const { collectionid: collectionId } = urlInfo.query || {};
  const activeIndex = computed(() => state.activeIndex);
  let tempReportData: IHashMap = {
    playLoops: 0,
    reported: false,
    playEnd: false,
    previousFeed: feed.value,
  };
  watch(feed, (newFeed, oldFeed) => {
    tempReportData.previousFeed = oldFeed;
  });
  watch(activeIndex, () => {
    const { playerRef } = params;
    if (!playerRef.value) return;
    swiperItemReport.onWatchActiveIndex();
  });
  const swiperItemReport = {
    tempReportData,
    setTempReportData(reportData: IHashMap) {
      tempReportData = {
        ...tempReportData,
        ...reportData,
      };
    },
    mountedReport() {
      if (IS_SSR) return;
      const { growthTestData, isFullScreen, isDrama, dramaList, activeIndex } = state;

      let dramaId = '';
      if (isDrama) {
        dramaId = dramaList[activeIndex]?.id;
      }
      report('video.exposure', {
        ...feedReportOpt,
        type: { // 灯塔上报参数
          is_new: growthTestData?.playBtnPullNew ? 1 : 2,
          is_call: growthTestData?.playBtnPullOld ? 1 : 2,
          truexp: growthTestData?.strategyID ? 1 : 2,
          play_type: 2,
          point: 1,
          feed_id: feed.value?.id,
          collection_style: growthTestData?.isNewUI ? 2 : 1,
          jump_miniApp: getMiniAppReportValue(EMiniAppPosition.video),
          num: feed.value?.index,
          play_status: isFullScreen ? 2 : 1,
          collection_id: collectionId,
          micro_drama_id: dramaId,
        },
      });
    },
    onPlay(isOnlyOnce = false) {
      const { growthTestData, isFullScreen, isDrama, dramaList, activeIndex } = state;
      const { playEnd, reported } = tempReportData;

      let dramaId = '';
      if (isDrama) {
        dramaId = dramaList[activeIndex]?.id;
      }

      report('video.play', {
        ...feedReportOpt,
        type: { // 灯塔上报参数
          is_new: growthTestData?.playBtnPullNew ? 1 : 2,
          is_call: growthTestData?.playBtnPullOld ? 1 : 2,
          truexp: growthTestData?.strategyID ? 1 : 2,
          play_type: isOnlyOnce ? 4 : 2,  // 默认 play_type 传 2，视频播放后，仅上传一次 4
          point: 1,
          feed_id: feed.value?.id,
          collection_style: growthTestData?.isNewUI ? 2 : 1,
          jump_miniApp: getMiniAppReportValue(EMiniAppPosition.video),
          num: feed.value?.index,
          play_status: isFullScreen ? 2 : 1,
          collection_id: collectionId,
          micro_drama_id: dramaId,
        },
      });

      if (playEnd) {
        tempReportData.playEnd = false;
        tempReportData.playLoops += 1;
      }
      if (!reported) {
        tempReportData.reported = true;
        console.log('report play.start');
        report('play.start', playReportOpt);
      }
    },
    onPause() {
      const { growthTestData, isFullScreen, isDrama, dramaList, activeIndex } = state;

      let dramaId = '';
      if (isDrama) {
        dramaId = dramaList[activeIndex]?.id;
      }

      report('video.pause', {
        ...feedReportOpt,
        type: {
          is_new: growthTestData?.playBtnPullNew ? 1 : 2,
          is_call: growthTestData?.playBtnPullOld ? 1 : 2,
          truexp: growthTestData?.strategyID ? 1 : 2,
          play_type: 2,
          point: 1,
          feed_id: feed.value?.id,
          collection_style: growthTestData?.isNewUI ? 2 : 1,
          jump_miniApp: getMiniAppReportValue(EMiniAppPosition.video),
          num: feed.value?.index,
          play_status: isFullScreen ? 2 : 1,
          collection_id: collectionId,
          micro_drama_id: dramaId,
        },
      });
    },
    clickShowBtn(showRecInPlayPage: boolean) {
      const { showCollection } = state;
      if (showRecInPlayPage) {
        report('video.fold.click', {
          ...feedReportOpt,
          type: { point: showCollection ? 2 : 1 },
        });
      }
      report('collection.fold.click', {
        ...feedReportOpt,
        type: {
          point: showCollection ? 2 : 1,
        },
      });
    },
    playEnd(useOldReportOptions?: boolean) {
      const { playerRef, currentTime } = params;
      const { previousFeed, playLoops } = tempReportData;
      if (!playerRef.value) return;
      const duration = Math.round((playLoops - 1)
      * playerRef.value.player?.duration()
      * 10e+2 + currentTime.value * 10e+2);
      const options = useOldReportOptions ? {
        video_id: previousFeed?.id,
        owner_id: previousFeed?.posterId,
        video_length: previousFeed?.video?.duration,
      } : playReportOpt;
      report('play.end', {
        ...options,
        duration,
        last_location: Math.round(currentTime.value * 10e+2),
      });
    },
    onEnded(onEndedParams: OnEndedParams) {
      const { isLastFeed } = onEndedParams;
      const { playerRef } = params;
      const { showCollection } = state;
      if (!playerRef.value) return;
      if (showCollection && isLastFeed) {
        swiperItemReport.playEnd();
      } else {
        swiperItemReport.playEnd();
        tempReportData.playLoops = 0;
        tempReportData.reported = false;
      }
      tempReportData.playEnd = true;
    },
    onWatchActiveIndex() {
      const { isNewUI } = state;
      const { playEnd } = tempReportData;
      if (!playEnd) {
        const useOldReportOptions = !isNewUI;
        swiperItemReport.playEnd(useOldReportOptions);
      }
      // 滑动后重置播放次数，重新上报play.start
      tempReportData.playLoops = 0;
      tempReportData.reported = false;
      tempReportData.playEnd = true;
    },
  };
  return swiperItemReport;
};
